/* LOONMOON country accordion: progressive enhancement of QW-1J country nodes. */
(() => {
  'use strict';
  const namespace = 'lmCountryAccordion';
  if (window[namespace]) {
    window[namespace].init();
    return;
  }
  const selector = 'li.currency form.localization-form > ul[data-lm-country-accordion]';
  const states = new WeakMap();
  const liveStates = new Set();
  let sequence = 0;
  let scheduled = false;
  const frame = (callback) => window.requestAnimationFrame(callback);
  const id = () => {
    let value;
    do { value = 'lm-country-region-' + (++sequence); }
    while (document.getElementById(value + '-button') || document.getElementById(value + '-panel'));
    return value;
  };

  function measure(state) {
    if (!state.root.isConnected) return;
    for (const group of state.groups) {
      group.item.classList.remove('lm-country-sticky');
      group.item.dataset.lmSticky = 'DISABLED BY SPACE/CONTEXT GUARD';
    }
    state.scroller = null;
    const open = state.groups.find((group) => !group.panel.hidden);
    if (!open || !open.button.getClientRects().length) return;
    let candidate = open.heading.parentElement;
    while (candidate && candidate !== state.form.parentElement) {
      const style = getComputedStyle(candidate);
      if (/auto|scroll/.test(style.overflowY)) {
        state.scroller = candidate;
        break;
      }
      if (/hidden|clip/.test(style.overflowY)) return;
      candidate = candidate.parentElement;
    }
    const scroller = state.scroller;
    if (!scroller || scroller.clientHeight === 0) return;
    // Narrow viewports use normal-flow headings to avoid sticky text collisions.
    if (window.innerWidth < 360) return;
    const headingHeight = open.heading.getBoundingClientRect().height;
    const countryHeight = open.rows[0].getBoundingClientRect().height;
    if (!headingHeight || !countryHeight ||
        scroller.clientHeight - headingHeight < Math.max(countryHeight * 3, headingHeight * 2)) return;
    open.item.classList.add('lm-country-sticky');
    open.item.style.setProperty('--lm-country-heading-height', headingHeight + 'px');
    open.item.dataset.lmSticky = 'ENABLED';
  }

  function close(group) {
    if (group.panel.contains(document.activeElement)) group.button.focus({ preventScroll: true });
    group.panel.hidden = true;
    group.button.setAttribute('aria-expanded', 'false');
  }

  function activate(state, group) {
    const expanding = group.panel.hidden;
    for (const other of state.groups) close(other);
    if (expanding) {
      group.panel.hidden = false;
      group.button.setAttribute('aria-expanded', 'true');
    }
    group.button.focus({ preventScroll: true });
    measure(state);
    frame(() => {
      if (!state.root.isConnected) return;
      const scroller = state.scroller;
      if (!scroller) return;
      const top = scroller.getBoundingClientRect().top + scroller.clientTop;
      const buttonTop = group.button.getBoundingClientRect().top;
      if (buttonTop < top || buttonTop >= top + scroller.clientHeight) {
        scroller.scrollTop += buttonTop - top;
      }
    });
  }

  function keepFocusVisible(state, target) {
    measure(state);
    const group = state.groups.find((entry) => !entry.panel.hidden && entry.panel.contains(target));
    if (!group || !group.item.classList.contains('lm-country-sticky') || !state.scroller) return;
    const scroller = state.scroller;
    const top = scroller.getBoundingClientRect().top + scroller.clientTop;
    const safeTop = top + group.heading.getBoundingClientRect().height;
    const rect = target.getBoundingClientRect();
    if (rect.top < safeTop) scroller.scrollTop -= safeTop - rect.top;
    else if (rect.bottom > top + scroller.clientHeight) scroller.scrollTop += rect.bottom - top - scroller.clientHeight;
  }

  function restore(state) {
    state.observer?.disconnect();
    state.root.removeEventListener('click', state.click);
    state.root.removeEventListener('focusin', state.focus);
    // Original nodes, including their existing localization handlers, return to their original order.
    state.root.replaceChildren(...state.originalNodes);
    state.root.classList.remove('lm-country-enhanced');
    states.delete(state.root);
    liveStates.delete(state);
  }

  function initialize(root) {
    if (states.has(root)) return;
    // An enhanced subtree copied by a theme must get its own controls and IDs.
    if (root.classList.contains('lm-country-enhanced')) {
      for (const group of Array.from(root.querySelectorAll(':scope > .lm-country-region'))) {
        const label = document.createElement('li');
        label.className = 'country-region-label';
        const span = document.createElement('span');
        span.textContent = group.querySelector('.lm-country-region-button')?.textContent.trim() || '';
        label.append(span);
        root.insertBefore(label, group);
        for (const row of Array.from(group.querySelectorAll('.lm-country-region-countries > li'))) root.insertBefore(row, group);
        group.remove();
      }
      root.classList.remove('lm-country-enhanced');
    }
    const originalNodes = Array.from(root.childNodes);
    const children = Array.from(root.children);
    const countryRows = children.filter((row) => row.querySelector(':scope > a[data-value]'));
    const anchors = countryRows.map((row) => row.querySelector(':scope > a[data-value]'));
    const codes = anchors.map((anchor) => anchor.dataset.value);
    const labels = children.filter((row) => row.classList.contains('country-region-label'));
    if (labels.length !== 6 || codes[0] !== 'CA' || codes[1] !== 'US' ||
        codes.some((code) => !/^[A-Z]{2}$/.test(code)) || new Set(codes).size !== codes.length ||
        anchors.length !== root.querySelectorAll('a[data-value]').length) return;
    const groups = labels.map((label) => {
      const rows = [];
      let next = label.nextElementSibling;
      while (next && !next.classList.contains('country-region-separator') && !next.classList.contains('country-region-label')) {
        if (!next.querySelector(':scope > a[data-value]')) throw new Error('Unexpected country row');
        rows.push(next);
        next = next.nextElementSibling;
      }
      if (!rows.length || !label.textContent.trim()) throw new Error('Empty country region');
      return { label, rows };
    });
    if (groups.reduce((sum, group) => sum + group.rows.length, 2) !== anchors.length) return;
    const state = { root, form: root.closest('form'), originalNodes, groups, scroller: null };
    try {
      for (const group of groups) {
        const identifier = id();
        group.item = document.createElement('li');
        group.item.className = 'lm-country-region';
        // The theme decorates every native footer heading with a navigation link.
        // ARIA heading semantics avoid that decoration and preserve a labelled heading.
        group.heading = document.createElement('div');
        group.heading.setAttribute('role', 'heading');
        group.heading.setAttribute('aria-level', '3');
        group.heading.className = 'lm-country-region-heading';
        group.button = document.createElement('button');
        group.button.type = 'button';
        group.button.className = 'lm-country-region-button';
        group.button.textContent = group.label.textContent.trim();
        group.button.id = identifier + '-button';
        group.button.setAttribute('aria-expanded', 'false');
        group.button.setAttribute('aria-controls', identifier + '-panel');
        group.panel = document.createElement('div');
        group.panel.className = 'lm-country-region-panel';
        group.panel.id = identifier + '-panel';
        group.panel.hidden = true;
        group.panel.setAttribute('aria-labelledby', group.button.id);
        const list = document.createElement('ul');
        list.className = 'lm-country-region-countries';
        group.heading.append(group.button);
        group.panel.append(list);
        group.item.append(group.heading, group.panel);
        root.insertBefore(group.item, group.label);
        group.label.remove();
        for (const row of group.rows) list.append(row);
      }
      const after = Array.from(root.querySelectorAll('a[data-value]'));
      if (after.length !== anchors.length || after.some((anchor, index) => anchor !== anchors[index])) throw new Error('Country integrity mismatch');
      state.click = (event) => {
        const target = event.target.closest('.lm-country-region-button');
        const group = groups.find((entry) => entry.button === target);
        if (group) activate(state, group);
      };
      state.focus = (event) => frame(() => keepFocusVisible(state, event.target));
      root.addEventListener('click', state.click);
      root.addEventListener('focusin', state.focus);
      root.classList.add('lm-country-enhanced');
      const active = groups.find((group) => group.rows.some((row) => row.matches('.active, [aria-current="true"]') || row.querySelector('a.active, a[aria-current="true"]')));
      if (active) {
        active.panel.hidden = false;
        active.button.setAttribute('aria-expanded', 'true');
      }
      states.set(root, state);
      liveStates.add(state);
      if ('ResizeObserver' in window) {
        state.observer = new ResizeObserver(() => frame(() => measure(state)));
        state.observer.observe(state.form);
        state.observer.observe(root);
        groups.forEach((group) => state.observer.observe(group.heading));
      }
      measure(state);
    } catch (error) {
      restore(state);
      console.warn('LOONMOON country accordion retained the grouped selector.', error);
    }
  }

  function init() {
    for (const state of liveStates) {
      if (!state.root.isConnected) {
        state.observer?.disconnect();
        liveStates.delete(state);
      }
    }
    for (const root of document.querySelectorAll(selector)) {
      try { initialize(root); }
      catch (error) { console.warn('LOONMOON country accordion validation failed; grouped selector retained.', error); }
    }
  }

  window[namespace] = Object.freeze({ init, restore: (root) => { const state = states.get(root); if (state) restore(state); } });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => frame(init));
  document.addEventListener('click', () => frame(() => liveStates.forEach(measure)));
  window.addEventListener('resize', () => frame(() => liveStates.forEach(measure)), { passive: true });
  const observe = () => {
    init();
    new MutationObserver((records) => {
      if (!records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1 &&
          (node.matches(selector) || node.querySelector(selector))))) return;
      if (!scheduled) {
        scheduled = true;
        frame(() => { scheduled = false; init(); });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe, { once: true });
  else observe();
})();
